/**
 * ColorPickerControls  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useState } from "react";

import { BEM } from "@adapptio/lib-components";
import { isFunction } from "@adapptio/lib-shared";

import { FieldGroup, FieldSelect, IFieldSelectProps } from "../../fields";
import { COLOR_CODE_TYPE, ColorInput, HEX, IHSLA, IRGBA } from "./ColorPickerTypes";
import { useDeepCompareMemo } from "use-deep-compare";
import { getColorUtils } from "./colorUtils";
import { ColorPickerInput, IColorPickerInputProps } from "./ColorPickerInput";
import { TSelectOption } from "../Select";

export interface IColorPickerControlsProps {
	className?: string;
	color?: ColorInput;
	onChange?: (color: string | ColorInput) => void;
}

const options: TSelectOption[] = [
	{
		label: "HEX",
		value: COLOR_CODE_TYPE.HEX
	},
	{
		label: "RGBA",
		value: COLOR_CODE_TYPE.RGBA
	},
	{
		label: "HSLA",
		value: COLOR_CODE_TYPE.HSLA
	}
];

export const ColorPickerControls: React.FC<IColorPickerControlsProps> = ({ className, color, onChange }) => {
	const [ selectedCodeType, setSelectedCodeType ] = useState<COLOR_CODE_TYPE>(COLOR_CODE_TYPE.HEX);
	const colorUtils = useDeepCompareMemo(() => getColorUtils(color), [ color ]);
	const [ inputValue, setInputValue ] = useState<Record<COLOR_CODE_TYPE, ColorInput>>({
		[COLOR_CODE_TYPE.HEX]: colorUtils.toHexString() as HEX,
		[COLOR_CODE_TYPE.RGBA]: colorUtils.toRgb(),
		[COLOR_CODE_TYPE.HSLA]: colorUtils.toHsl()
	});

	const baseClass = BEM("edt-color-picker-controls");

	const selectCodeType = useCallback<IFieldSelectProps["onChange"]>((value) => {
		setSelectedCodeType(value as COLOR_CODE_TYPE);
	}, []);

	const getChangeHandler = useCallback<
		(
			codeType: COLOR_CODE_TYPE,
			property?: keyof IRGBA | keyof IHSLA
		) => IColorPickerInputProps["onChange"]
	>(
		(codeType, property) => (value) => {
			const colorValue: ColorInput = {
				[COLOR_CODE_TYPE.HEX]: value as HEX,
				[COLOR_CODE_TYPE.RGBA]: {
					...colorUtils.toRgb(),
					[property]: value
				},
				[COLOR_CODE_TYPE.HSLA]: {
					...colorUtils.toHsl(),
					[property]: value
				}
			}[codeType];

			if (isFunction(onChange)) {
				onChange(colorValue);
			}
		},
		[ onChange, colorUtils ]
	);

	if (className) {
		baseClass.add(className);
	}

	return (
		<FieldGroup className={baseClass.toClassName()}>
			<FieldSelect
				value={selectedCodeType}
				options={options}
				onChange={selectCodeType}
				className={baseClass.element("code-type").toClassName()}
			/>
			{
				{
					[COLOR_CODE_TYPE.HEX]: (
						<>
							<ColorPickerInput
								key="hex"
								value={colorUtils.toHexString()}
								onChange={getChangeHandler(COLOR_CODE_TYPE.HEX)}
								className={baseClass.element("input").toClassName()}
								maxLength={7}
								autoFocus
								autoFocusSelect
							/>
						</>
					),
					[COLOR_CODE_TYPE.RGBA]: (
						<>
							<ColorPickerInput
								type="number"
								min={0}
								max={255}
								value={`${colorUtils.toRgb().r}`}
								onChange={getChangeHandler(COLOR_CODE_TYPE.RGBA, "r")}
								className={baseClass.element("input").toClassName()}
							/>
							<ColorPickerInput
								type="number"
								min={0}
								max={255}
								value={`${colorUtils.toRgb().g}`}
								onChange={getChangeHandler(COLOR_CODE_TYPE.RGBA, "g")}
								className={baseClass.element("input").toClassName()}
							/>
							<ColorPickerInput
								type="number"
								min={0}
								max={255}
								value={`${colorUtils.toRgb().b}`}
								onChange={getChangeHandler(COLOR_CODE_TYPE.RGBA, "b")}
								className={baseClass.element("input").toClassName()}
							/>
							<ColorPickerInput
								type="number"
								min={0}
								max={1}
								step="0.01"
								value={`${colorUtils.toRgb().a}`}
								onChange={getChangeHandler(COLOR_CODE_TYPE.RGBA, "a")}
								className={baseClass.element("input").toClassName()}
							/>
						</>
					),
					[COLOR_CODE_TYPE.HSLA]: (
						<>
							<ColorPickerInput
								type="number"
								min={0}
								max={360}
								value={`${Math.round(colorUtils.toHsl().h)}`}
								onChange={getChangeHandler(COLOR_CODE_TYPE.HSLA, "h")}
								className={baseClass.element("input").toClassName()}
							/>
							<ColorPickerInput
								type="number"
								min={0}
								max={100}
								suffix="%"
								value={`${Math.round(colorUtils.toHsl().s * 100)}`}
								onChange={getChangeHandler(COLOR_CODE_TYPE.HSLA, "s")}
								className={baseClass.element("input").toClassName()}
							/>
							<ColorPickerInput
								type="number"
								min={0}
								max={100}
								suffix="%"
								value={`${Math.round(colorUtils.toHsl().l * 100)}`}
								onChange={getChangeHandler(COLOR_CODE_TYPE.HSLA, "l")}
								className={baseClass.element("input").toClassName()}
							/>
							<ColorPickerInput
								type="number"
								min={0}
								max={1}
								step="0.01"
								value={`${colorUtils.toHsl().a}`}
								onChange={getChangeHandler(COLOR_CODE_TYPE.HSLA, "a")}
								className={baseClass.element("input").toClassName()}
							/>
						</>
					)
				}[selectedCodeType]
			}
		</FieldGroup>
	);
};
